/* External */
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import {
  differenceInMinutes,
  isBefore,
  isWithinInterval,
  setSeconds,
  format,
} from 'date-fns';
import classNames from 'classnames';

/* Styles */
import './LiveBadgeTag.scss';

/* Other */
import { getDaySelectedText } from '../../utils';

function LiveBadgeTag({
  className, endTime, startTime, intl: { formatDate, formatMessage },
}) {
  const [isLiveContent, setIsLiveContent] = useState(true);

  const getScheduleText = useCallback(() => {
    if (startTime) {
      const day = getDaySelectedText(startTime, formatMessage, formatDate);
      const start = format(new Date(startTime), 'HH:mm');
      return `${day} • ${start}`;
    }
    return '';
  }, [startTime, formatMessage, formatDate]);

  const checkIsLiveContent = useCallback(() => {
    const currentDate = new Date();
    const start = setSeconds(new Date(startTime), 0);
    const end = setSeconds(new Date(endTime), 0);
    let live = false;

    if (isBefore(start, end) && isWithinInterval(currentDate, { start, end })) {
      live = !!(differenceInMinutes(end, currentDate) + 1);
    }

    setIsLiveContent(live);
    return live;
  }, [startTime, endTime]);

  useEffect(() => {
    if (startTime && endTime) {
      checkIsLiveContent();

      const interval = setInterval(() => {
        const live = checkIsLiveContent();
        if (!live) clearInterval(interval);
      }, 1000);

      return () => clearInterval(interval);
    }

    return () => {};
  }, [checkIsLiveContent, startTime, endTime]);

  return (
    <div className={classNames('dtv-badge-live-tag', className)}>
      <div
        className={classNames('dtv-badge-live-tag-indicator', {
          'dtv-badge-live-tag-indicator_active': isLiveContent,
        })}
      />
      <h4 className="dtv-badge-live-tag-schedule">{getScheduleText()}</h4>
    </div>
  );
}

LiveBadgeTag.propTypes = {
  className: PropTypes.string,
  endTime: PropTypes.string,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
    formatDate: PropTypes.func,
  }).isRequired,
  startTime: PropTypes.string,
};

LiveBadgeTag.defaultProps = {
  className: '',
  endTime: '',
  startTime: '',
};

export default injectIntl(LiveBadgeTag);
